/**
 * Emergency contacts — shared view + editor used by the admin user form, the
 * self profile, and (read-only) the org profile. A user can have several
 * contacts; each has a name, relationship (dropdown of common values + free
 * text when "Other"), phone and optional email.
 */
import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import {
  CALLABLES,
  EMERGENCY_CONTACT_RELATIONSHIPS,
  type EmergencyContact,
  type SelfUpdateProfileInput,
  type SelfUpdateProfileResponse,
} from '@daaily/shared';
import { Modal } from '../components/Modal';
import { useFireAndForget } from '../components/BackgroundSubmit';

const PREDEFINED = EMERGENCY_CONTACT_RELATIONSHIPS.filter((r) => r !== 'Other');

/** Drop incomplete rows + trim. A contact needs at least a name + relationship. */
export function cleanEmergencyContacts(list: EmergencyContact[]): EmergencyContact[] {
  return list
    .map((c) => ({
      name: c.name.trim(),
      relationship: c.relationship.trim(),
      phone: c.phone.trim(),
      email: c.email?.trim() ? c.email.trim() : null,
    }))
    .filter((c) => c.name && c.relationship);
}

const EMPTY: EmergencyContact = { name: '', relationship: '', phone: '', email: '' };

// ── Read-only view ──────────────────────────────────────────────────────────
export function EmergencyContactsView({ contacts }: { contacts: EmergencyContact[] }) {
  if (!contacts || contacts.length === 0) {
    return <p className="text-sm text-fg-faint">None added.</p>;
  }
  return (
    <div className="space-y-3">
      {contacts.map((c, i) => (
        <div key={i} className="border border-line rounded-md p-3">
          <div className="text-sm font-medium text-fg">
            {c.name} {c.relationship ? <span className="text-fg-faint font-normal">· {c.relationship}</span> : null}
          </div>
          <dl className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {c.phone && (
              <div className="flex justify-between gap-3">
                <dt className="text-fg-faint">Phone</dt>
                <dd className="text-fg">{c.phone}</dd>
              </div>
            )}
            {c.email && (
              <div className="flex justify-between gap-3">
                <dt className="text-fg-faint">Email</dt>
                <dd className="text-fg truncate">{c.email}</dd>
              </div>
            )}
          </dl>
        </div>
      ))}
    </div>
  );
}

// ── Editor ──────────────────────────────────────────────────────────────────
export function EmergencyContactsEditor({
  value,
  onChange,
  max = 6,
}: {
  value: EmergencyContact[];
  onChange: (next: EmergencyContact[]) => void;
  max?: number;
}) {
  const set = (i: number, patch: Partial<EmergencyContact>) =>
    onChange(value.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, { ...EMPTY }]);

  return (
    <div className="space-y-4">
      {value.map((c, i) => {
        const isOther = c.relationship !== '' && !PREDEFINED.includes(c.relationship as (typeof PREDEFINED)[number]);
        return (
          <div key={i} className="border border-line rounded-md p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-fg-faint">Contact {i + 1}</span>
              <button type="button" onClick={() => remove(i)} className="text-xs text-fg-faint hover:text-fg">
                Remove
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-fg-faint mb-1">Name</label>
                <input
                  type="text"
                  value={c.name}
                  onChange={(e) => set(i, { name: e.target.value })}
                  maxLength={140}
                  className="w-full"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-xs text-fg-faint mb-1">Relationship</label>
                <select
                  value={isOther ? 'Other' : c.relationship}
                  onChange={(e) =>
                    set(i, { relationship: e.target.value === 'Other' ? '' : e.target.value })
                  }
                  className="w-full"
                >
                  <option value="">— pick —</option>
                  {PREDEFINED.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                  <option value="Other">Other…</option>
                </select>
                {isOther && (
                  <input
                    type="text"
                    value={c.relationship}
                    onChange={(e) => set(i, { relationship: e.target.value })}
                    maxLength={60}
                    className="w-full mt-2"
                    placeholder="Specify relationship"
                  />
                )}
              </div>
              <div>
                <label className="block text-xs text-fg-faint mb-1">Phone</label>
                <input
                  type="text"
                  value={c.phone}
                  onChange={(e) => set(i, { phone: e.target.value })}
                  maxLength={40}
                  className="w-full"
                  placeholder="+41 …"
                />
              </div>
              <div>
                <label className="block text-xs text-fg-faint mb-1">Email (optional)</label>
                <input
                  type="email"
                  value={c.email ?? ''}
                  onChange={(e) => set(i, { email: e.target.value })}
                  maxLength={140}
                  className="w-full"
                  placeholder="name@example.com"
                />
              </div>
            </div>
          </div>
        );
      })}
      {value.length < max && (
        <button type="button" onClick={add} className="btn-secondary text-sm">
          + Add contact
        </button>
      )}
    </div>
  );
}

// ── Self-service modal ────────────────────────────────────────────────────────
// Partial input — this modal only sends emergencyContacts; the callable treats
// absent groups as unchanged.
const callSelfUpdate = httpsCallable<Partial<SelfUpdateProfileInput>, SelfUpdateProfileResponse>(
  functions,
  CALLABLES.SELF_UPDATE_PROFILE,
);

export function EmergencyContactsModal({
  initial,
  onClose,
}: {
  initial: EmergencyContact[];
  onClose: () => void;
}) {
  const [list, setList] = useState<EmergencyContact[]>(
    initial.length ? initial.map((c) => ({ ...c, email: c.email ?? '' })) : [{ ...EMPTY }],
  );
  const fireAndForget = useFireAndForget();

  // Close at once and save in the background (the profile page is live from Firestore, so it
  // updates itself). The payload is captured, so Retry works after this modal has unmounted.
  const save = () => {
    const payload = cleanEmergencyContacts(list);
    const fire = () => fireAndForget(
      () => callSelfUpdate({ emergencyContacts: payload }),
      { label: 'Saving your emergency contacts', successLabel: 'Saved — HR has been notified', confirm: true, onRetry: fire },
    );
    fire();
    onClose();
  };

  return (
    <Modal open onClose={onClose} title="Edit emergency contacts">
      <p className="text-xs text-fg-faint mb-4">Who we should reach in an emergency. Changes notify HR.</p>

      <EmergencyContactsEditor value={list} onChange={setList} />

      <div className="mt-6 flex items-center justify-end gap-3">
        <button type="button" onClick={onClose} className="btn-secondary text-sm">
          Cancel
        </button>
        <button type="button" onClick={save} className="btn-primary text-sm">
          Save changes
        </button>
      </div>
    </Modal>
  );
}
